import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import PatientsV2 from "@/pages/sections/PatientsV2";
import ChartV2 from "@/pages/ChartV2";
import ClaimWorkspace from "@/components/claim/ClaimWorkspace";
import { ChromeContext } from "@/lib/chromeContext";
import { useClient } from "@/lib/clientContext";

const STORE_KEY = "revq.chartTabsV2";

const titleCase = (s) =>
  String(s || "").replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

const nameOf = (pt) => {
  const n = [pt.first_name, pt.last_name].filter(Boolean).join(" ").trim() || pt.name || pt.patient_name || "";
  return titleCase(n) || "Patient";
};

const initialsOf = (name) =>
  (String(name).split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("") || "PT").toUpperCase();

const demoOf = (pt) => {
  const a = pt.age != null && pt.age !== "" ? `${pt.age}y` : "";
  const s = (pt.gender || "").charAt(0).toUpperCase();
  return [a, s].filter(Boolean).join(" · ");
};

// Inline browser-style tab strip (revQ Patients v2 reference): flat tabs joined to
// the pane by an indigo underline, a Home "Patients" tab, avatar initials + name
// (age/sex on the active tab), an amber dot while a chart has an encounter open,
// close-on-hover, a "+" to open another, and an "N open" counter. Strip only
// appears once at least one chart is open.
const PW_CSS = `
.pw-root{--indigo:#5856D6;display:flex;flex-direction:column;height:100%;min-height:0;min-width:0;max-width:100%;overflow:hidden;}
.pw-root .tabstrip{background:#FFFFFF;display:flex;align-items:flex-end;padding:4px 10px 0;border-bottom:1px solid #E6E6E4;position:relative;z-index:5;flex:none;min-width:0;max-width:100%;overflow:visible;}
.pw-root .thome{flex:none;display:flex;align-items:center;gap:7px;height:32px;padding:0 13px;border:1px solid transparent;border-bottom:0;border-radius:7px 7px 0 0;background:none;cursor:pointer;font-size:12px;font-weight:650;color:#6C6C69;transition:color .15s,background .15s;}
.pw-root .thome:hover{color:#141412;}
.pw-root .thome.on{color:#141412;box-shadow:inset 0 -2px 0 var(--indigo);}
.pw-root .thome svg{width:15px;height:15px;fill:none;stroke:currentColor;stroke-width:1.6;}
.pw-root .tdiv{flex:none;width:1px;height:18px;background:#E6E6E4;margin:0 6px 8px;}
.pw-root .tstrip{display:flex;align-items:flex-end;gap:2px;overflow:hidden;flex:1;min-width:0;}
.pw-root .tab{flex:none;max-width:230px;display:flex;align-items:center;gap:8px;height:32px;padding:0 5px 0 9px;cursor:pointer;border:1px solid transparent;border-bottom:0;border-radius:7px 7px 0 0;background:none;color:#6C6C69;transition:background .15s,color .15s;}
.pw-root .tab:hover{background:rgba(15,16,18,.035);color:#141412;}
.pw-root .tab.on{color:#141412;box-shadow:inset 0 -2px 0 var(--indigo);}
.pw-root .tab .av{flex:none;width:21px;height:21px;border-radius:50%;background:#F3F3F1;border:1px solid #E6E6E4;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:750;color:#6C6C69;letter-spacing:.02em;transition:background .15s,color .15s,border-color .15s;}
.pw-root .tab.on .av{background:#ecebfb;border-color:#d7d5f5;color:var(--indigo);}
.pw-root .tab .tn{min-width:0;display:flex;flex-direction:column;line-height:1.2;text-align:left;}
.pw-root .tab .tn b{font-weight:650;font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pw-root .tab.on .tn b{font-weight:700;}
.pw-root .tab .tn i{display:none;font-style:normal;font-size:9px;color:#9B9B97;}
.pw-root .tab.on .tn i{display:block;}
.pw-root .tab .tx{flex:none;width:18px;height:18px;display:flex;align-items:center;justify-content:center;border:0;background:none;border-radius:5px;cursor:pointer;color:#9B9B97;opacity:0;transition:opacity .15s,background .15s;font-size:14px;line-height:1;}
.pw-root .tab:hover .tx{opacity:1;}
.pw-root .tab .tx:hover{background:rgba(0,0,0,.07);color:#141412;}
.pw-root .tab .dot{flex:none;width:18px;height:18px;display:flex;align-items:center;justify-content:center;}
.pw-root .tab .dot i{width:6px;height:6px;border-radius:50%;background:#F08C00;display:block;}
.pw-root .tab:hover .dot{display:none;}
.pw-root .tadd{flex:none;width:28px;height:28px;margin:0 0 3px 4px;display:flex;align-items:center;justify-content:center;border:1px solid transparent;background:none;border-radius:7px;cursor:pointer;color:#9B9B97;transition:background .15s,color .15s;}
.pw-root .tadd:hover{background:#F3F3F1;color:#141412;}
.pw-root .tadd svg{width:15px;height:15px;fill:none;stroke:currentColor;stroke-width:1.7;}
.pw-root .tcount{flex:none;margin:0 4px 9px 6px;font-size:9px;color:#9B9B97;white-space:nowrap;}
.pw-root .tovf-wrap{position:relative;flex:none;margin:0 2px 3px 2px;}
.pw-root .tovf{display:flex;align-items:center;gap:3px;height:28px;padding:0 7px;border:1px solid var(--hair,#E6E6E4);background:#fff;border-radius:7px;cursor:pointer;color:#6C6C69;transition:background .15s,color .15s,border-color .15s;}
.pw-root .tovf:hover,.pw-root .tovf.on{background:#F3F3F1;color:#141412;border-color:#D7D6D2;}
.pw-root .tovf .tovf-n{font-size:10px;font-weight:750;font-variant-numeric:tabular-nums;}
.pw-root .tovf svg{width:14px;height:14px;fill:none;stroke:currentColor;stroke-width:2.2;}
.pw-root .tovf .chev2{width:15px;height:15px;}
.pw-root .tovf-menu{position:absolute;top:34px;right:0;z-index:60;width:250px;max-height:60vh;overflow:auto;background:#fff;border:1px solid rgba(20,20,18,.12);border-radius:11px;box-shadow:0 22px 54px -20px rgba(20,20,18,.34),0 6px 16px -10px rgba(20,20,18,.16);padding:5px;animation:tovfIn .15s ease;}
@keyframes tovfIn{from{opacity:0;transform:translateY(-5px);}to{opacity:1;transform:none;}}
.pw-root .tovf-hd{font-size:8.5px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:#9B9B97;padding:6px 9px 5px;}
.pw-root .tovf-row{display:flex;align-items:center;gap:8px;padding:7px 8px;border-radius:8px;cursor:pointer;transition:background .12s;}
.pw-root .tovf-ck{flex:none;width:15px;display:flex;align-items:center;justify-content:center;}
.pw-root .tovf-ck svg{width:14px;height:14px;fill:none;stroke:var(--indigo,#5B4FC7);stroke-width:2.6;}
.pw-root .tovf-row:hover{background:#F6F5F3;}
.pw-root .tovf-row.on{background:#ecebfb;}
.pw-root .tovf-row.on .av{background:#ded9fb;border-color:#cfc9f5;color:var(--indigo);}
.pw-root .tovf-tn{flex:1;min-width:0;display:flex;flex-direction:column;line-height:1.25;}
.pw-root .tovf-tn b{font-weight:650;font-size:12px;color:#141412;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pw-root .tovf-tn i{font-style:normal;font-size:9px;color:#9B9B97;}
.pw-root .tovf-x{flex:none;width:20px;height:20px;display:flex;align-items:center;justify-content:center;border:0;background:none;border-radius:5px;color:#9B9B97;cursor:pointer;font-size:15px;line-height:1;transition:background .12s,color .12s;}
.pw-root .tovf-x:hover{background:rgba(0,0,0,.08);color:#141412;}
.pw-root .pw-canvas{position:relative;flex:1;min-height:0;}
.pw-root .pw-canvas.has-strip{background:#f5f5f7;}
.pw-root .pw-view{position:absolute;inset:0;}
.pw-root .pw-list{overflow:auto;padding:24px 28px 0;}
`;

export default function PatientsWorkspaceV2() {
  const { setSidebarCollapsed } = useContext(ChromeContext);
  const { selectedClientId } = useClient();
  const [tabs, setTabs] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || "[]"); } catch (e) { return []; }
  });
  const [active, setActive] = useState("patients"); // "patients" | uuid
  const [chartingMap, setChartingMap] = useState({}); // uuid -> bool (encounter open)

  // Open chart tabs belong to one tenant. If the selected client changes, drop tabs
  // from the previous tenant so another tenant's patients never leak in.
  const tabsOwner = useRef(null);
  useEffect(() => {
    if (!selectedClientId) return; // client not resolved yet
    if (tabsOwner.current == null) { tabsOwner.current = String(selectedClientId); return; }
    if (tabsOwner.current !== String(selectedClientId)) {
      tabsOwner.current = String(selectedClientId);
      setTabs([]);
      setActive("patients");
      setChartingMap({});
    }
  }, [selectedClientId]);

  useEffect(() => {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(tabs)); } catch (e) { console.warn("PatientsWorkspace: could not persist tabs", e); }
  }, [tabs]);

  // Collapse the revQ sidebar to icons only while the ACTIVE chart tab is charting.
  const collapsed = active !== "patients" && !!chartingMap[active];
  useEffect(() => { setSidebarCollapsed(collapsed); }, [collapsed, setSidebarCollapsed]);
  useEffect(() => () => setSidebarCollapsed(false), [setSidebarCollapsed]);

  const setCharting = useCallback((uuid, on) => {
    setChartingMap((m) => (m[uuid] === on ? m : { ...m, [uuid]: on }));
  }, []);

  const [claimQ, setClaimQ] = useState(null); // { items, index }

  const openChart = useCallback((pt, tab) => {
    const uuid = pt.chart_uuid || pt.chart_id;
    if (!uuid) return;
    const name = nameOf(pt);
    const req = tab && tab !== "focus" ? { tab, n: Date.now() } : null;
    setTabs((prev) => {
      const exists = prev.some((t) => t.uuid === uuid);
      if (exists) return req ? prev.map((t) => (t.uuid === uuid ? { ...t, targetTab: req } : t)) : prev;
      return [...prev, { uuid, name, initials: initialsOf(name), demo: demoOf(pt), targetTab: req }];
    });
    setActive(uuid);
  }, []);

  const closeTab = useCallback((uuid, e) => {
    if (e) e.stopPropagation();
    setChartingMap((m) => { const n = { ...m }; delete n[uuid]; return n; });
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.uuid === uuid);
      const next = prev.filter((t) => t.uuid !== uuid);
      setActive((cur) => (cur !== uuid ? cur : (next[idx - 1] || next[idx] || {}).uuid || "patients"));
      return next;
    });
  }, []);

  const hasStrip = tabs.length > 0;

  // Tab overflow (Premiere-style): instead of a horizontal scroll, tabs that don't
  // fit collapse into a chevron ▾ dropdown. We measure real tab widths, keep the
  // active tab pinned visible, and list the rest in the menu.
  const stripRef = useRef(null);
  const tabRefs = useRef({});
  const ovfBtnRef = useRef(null);
  const [maxVisible, setMaxVisible] = useState(999);
  const [ovfOpen, setOvfOpen] = useState(false);

  const measure = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    if (!tabs.length) { setMaxVisible(0); return; }
    const stripLeft = strip.getBoundingClientRect().left;
    // clamp to what's visible in the viewport (the workspace can be wider than the
    // window when many charts are mounted), reserving room for the trailing controls
    const avail = Math.max(0, Math.min(strip.clientWidth, window.innerWidth - stripLeft - 130));
    const TAB_W = 164; // generous per-tab estimate so visible tabs never clip
    const OVF_W = 48;  // the ▾ chevron slot
    let n = Math.floor(avail / TAB_W);
    if (n >= tabs.length) n = tabs.length;              // all fit, no chevron
    else n = Math.max(1, Math.floor((avail - OVF_W) / TAB_W)); // leave room for ▾
    setMaxVisible(n);
  }, [tabs]);

  useLayoutEffect(() => {
    measure();
    const strip = stripRef.current;
    if (!strip) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(strip);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    if (!ovfOpen) return;
    const onDoc = (e) => { if (!e.target.closest(".tovf-wrap")) setOvfOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [ovfOpen]);

  // which tabs render in the strip vs the overflow menu (active is always pinned visible)
  const visSet = new Set(tabs.slice(0, maxVisible).map((t) => t.uuid));
  const activeIsTab = tabs.some((t) => t.uuid === active);
  if (activeIsTab && !visSet.has(active) && maxVisible > 0) {
    const firstIds = tabs.slice(0, maxVisible - 1).map((t) => t.uuid);
    visSet.clear();
    firstIds.forEach((id) => visSet.add(id));
    visSet.add(active);
  }
  const overflowTabs = tabs.filter((t) => !visSet.has(t.uuid));

  return (
    <div className="pw-root" data-testid="patients-workspace-v2">
      <style>{PW_CSS}</style>

      {/* tab strip — only shown once at least one chart is open */}
      {hasStrip && (
        <div className="tabstrip" data-testid="chart-tab-strip">
          <button
            className={`thome ${active === "patients" ? "on" : ""}`}
            onClick={() => setActive("patients")}
            data-testid="chart-tab-patients"
            title="Patients"
          >
            <svg viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            Patients
          </button>
          <span className="tdiv" />
          <div className="tstrip" ref={stripRef}>
            {tabs.map((t) => {
              const on = active === t.uuid;
              const working = !!chartingMap[t.uuid];
              return (
                <div
                  key={t.uuid}
                  ref={(el) => { tabRefs.current[t.uuid] = el; }}
                  onClick={(e) => { if (e.target.closest(".tx")) return; setActive(t.uuid); }}
                  data-testid={`chart-tab-${t.uuid}`}
                  className={`tab ${on ? "on" : ""}`}
                  style={{ display: visSet.has(t.uuid) ? undefined : "none" }}
                  title={t.name}
                  role="button"
                  tabIndex={0}
                >
                  <span className="av">{t.initials || "PT"}</span>
                  <span className="tn">
                    <b>{t.name}</b>
                    {t.demo ? <i>{t.demo}</i> : null}
                  </span>
                  {working && (
                    <span className="dot" title="Encounter in progress"><i /></span>
                  )}
                  <button
                    onClick={(e) => closeTab(t.uuid, e)}
                    data-testid={`chart-tab-close-${t.uuid}`}
                    className="tx"
                    aria-label={`Close ${t.name}`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
          {overflowTabs.length > 0 && (
            <div className="tovf-wrap">
              <button
                ref={ovfBtnRef}
                className={`tovf ${ovfOpen ? "on" : ""}`}
                onClick={() => setOvfOpen((v) => !v)}
                data-testid="chart-tab-overflow"
                aria-label={`Show all ${tabs.length} open charts`}
                title="All open charts"
              >
                <svg viewBox="0 0 24 24" className="chev2"><path d="M13 6l6 6-6 6M5 6l6 6-6 6" /></svg>
              </button>
              {ovfOpen && (
                <div className="tovf-menu" data-testid="chart-tab-overflow-menu">
                  {tabs.map((t) => (
                    <div
                      key={t.uuid}
                      className={`tovf-row ${active === t.uuid ? "on" : ""}`}
                      onClick={(e) => { if (e.target.closest(".tovf-x")) return; setActive(t.uuid); setOvfOpen(false); }}
                      data-testid={`chart-tab-overflow-${t.uuid}`}
                      role="button"
                      tabIndex={0}
                    >
                      <span className="tovf-ck" aria-hidden="true">
                        {active === t.uuid ? (<svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>) : null}
                      </span>
                      <span className="av">{t.initials || "PT"}</span>
                      <span className="tovf-tn">
                        <b>{t.name}</b>
                        {t.demo ? <i>{t.demo}</i> : null}
                      </span>
                      {!!chartingMap[t.uuid] && <span className="dot" title="Encounter in progress"><i /></span>}
                      <button className="tovf-x" onClick={(e) => closeTab(t.uuid, e)} aria-label={`Close ${t.name}`}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <span className="tcount" data-testid="chart-tab-count">{tabs.length} open</span>
          <button
            className="tadd"
            onClick={() => setActive("patients")}
            data-testid="chart-tab-add"
            aria-label="Open another chart"
            title="Open another chart"
          >
            <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
          </button>
        </div>
      )}

      {/* canvas: Patients list stays mounted; each opened chart stays mounted for instant switching */}
      <div className={`pw-canvas ${hasStrip ? "has-strip" : ""}`}>
        <div key="patients-home" className="pw-view pw-list" style={{ display: active === "patients" && !claimQ ? "block" : "none" }}>
          <PatientsV2 onOpenChart={openChart} onOpenClaim={(items, index) => setClaimQ({ items, index })} />
        </div>
        <div key="claim-workspace" className="pw-view" style={{ display: claimQ && active === "patients" ? "block" : "none" }} data-testid="patients-claim-workspace">
          {claimQ ? (
            <ClaimWorkspace
              items={claimQ.items}
              index={claimQ.index}
              onIndex={(i) => setClaimQ((q) => ({ ...q, index: i }))}
              backLabel="Patients"
              onClose={() => setClaimQ(null)}
              onChanged={() => {}}
            />
          ) : null}
        </div>
        {tabs.map((t) => (
          <div key={t.uuid} className="pw-view" style={{ display: active === t.uuid ? "block" : "none" }}>
            <ChartV2 embedded uuid={t.uuid} targetTab={t.targetTab} onChartingChange={(on) => setCharting(t.uuid, on)} />
          </div>
        ))}
      </div>
    </div>
  );
}
